import type { ComponentType } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import useTimerState from '@/components/utils/useTimerState';
import type { TimerDisplayShape } from '@/utils/timerDisplay';
import { useTimerDisplay } from './useTimerDisplayMode';
import TimerRing from './TimerRing';
import TimerDisc from './TimerDisc';
import TimerWedge from './TimerWedge';
import TimerDial from './TimerDial';
import TimerBar from './TimerBar';
import TimerBarVertical from './TimerBarVertical';
import TimerDigital from './TimerDigital';
import type { TimerViewProps } from './timerView';

/**
 * Nedräkningstimern för den pågående aktiviteten — VÄXELN mellan formerna.
 *
 * Namnet TimerCircle behålls medvetet: komponenten är den enda ingången för
 * barnvyns timer (ActivityInProgress) och exporteras via components/child-barrelen.
 * Formen och siffrorna väljs av HANDLEDAREN (useTimerDisplay → kontot, speglat i
 * localStorage) och kombineras fritt: ring, fylld cirkel, tårtbit, urtavla med visare,
 * liggande eller stående stapel — med eller utan mm:ss. Utan figur ('none') är det
 * siffrorna som är klockan. Default ring med siffror = oförändrat beteende.
 *
 * Tiden drivs av den DELADE useTimerState-hooken (clearInterval i dess cleanup —
 * vi bygger ALDRIG egen setInterval), och hooken ligger HÄR i växeln, inte i
 * renderarna. Därför fortsätter nedräkningen orörd när barnet byter visningsform
 * mitt i en aktivitet: bara det som ritas byts ut, aldrig timerstaten. Renderarna
 * är rena och får återstående tid som prop.
 */

type TimerCircleProps = {
  /** Aktivitetens längd i minuter (utgångspunkt). */
  minutes: number;
  /**
   * Extra sekunder ovanpå minuterna. Används av rutinerna, där ett steg kan ha ärvt
   * tid från föregående steg och därför sällan landar på en hel minut.
   */
  seconds?: number;
  /** Om timern räknar ner just nu. */
  isRunning: boolean;
  /** localStorage-nyckel (timer-{id}) — delas med övriga timervyer. */
  timerId?: number | string;
  /** Anropas vid tap på timern (starta/pausa). */
  onToggle?: () => void;
  /** Anropas när tiden når 0. */
  onTimerEnd?: () => void;
  /**
   * Timerns accentfärg. Utelämnad — och det är det normala — följer klockan det VALDA
   * FÄRGSCHEMAT via temats primärfärg (App.tsx synkar paletten mot schemat). Propen
   * finns kvar för en vy som medvetet vill avvika.
   */
  color?: string;
};

const TIMER_VIEWS: Record<TimerDisplayShape, ComponentType<TimerViewProps>> = {
  ring: TimerRing,
  disc: TimerDisc,
  wedge: TimerWedge,
  dial: TimerDial,
  bar_h: TimerBar,
  bar_v: TimerBarVertical,
  // Ingen figur: då ÄR siffrorna klockan, och TimerDigital ritar dem stora.
  none: TimerDigital,
};

const TimerCircle = ({
  minutes,
  seconds = 0,
  isRunning,
  timerId,
  onToggle,
  onTimerEnd,
  color,
}: TimerCircleProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { shape, digits } = useTimerDisplay();

  const { time, timerDone } = useTimerState({ minutes, seconds, isRunning, onTimerEnd, timerId });

  const totalSeconds = Math.max(minutes * 60 + seconds, 1);

  const labelKey = timerDone
    ? 'child.timer.done'
    : isRunning
    ? 'child.timer.running'
    : time < totalSeconds
    ? 'child.timer.paused'
    : 'child.timer.tapToStart';

  const TimerView = TIMER_VIEWS[shape];

  return (
    <TimerView
      time={time}
      totalSeconds={totalSeconds}
      label={String(t(labelKey))}
      color={color ?? theme.palette.primary.main}
      onToggle={onToggle}
      showDigits={digits}
    />
  );
};

export default TimerCircle;
